/**
 * DesignSystemPage — /design-system
 *
 * Single-page showcase of the Premium Publishing UI token system and primitives.
 * Use as a contributor reference; live URL when deployed.
 *
 * Renders:
 *   - Color swatches (paper, ink, walnut, proof, verify, flag)
 *   - Type scale (display + UI + mono)
 *   - Spacing + radii + elevation samples
 *   - Buttons / Cards / Inputs / Badges / Eyebrows
 *   - One full chapter card pulling everything together
 */

import { useState } from "react";
import { Button, Card, Input, Badge, Eyebrow } from "../components/ui";
import { tokens } from "../design-system/tokens";

const SWATCHES: Array<{ group: string; entries: Array<[string, string]> }> = [
  {
    group: "Paper",
    entries: [
      ["paper",        tokens.colors.paper],
      ["paper-2",      tokens.colors.paper2],
      ["paper-3",      tokens.colors.paper3],
      ["paper-raised", tokens.colors.paperRaised],
      ["margin",       tokens.colors.margin],
      ["tape",         tokens.colors.tape],
    ],
  },
  {
    group: "Ink",
    entries: [
      ["ink-1", tokens.colors.ink],
      ["ink-2", tokens.colors.ink2],
      ["ink-3", tokens.colors.ink3],
      ["ink-4", tokens.colors.ink4],
    ],
  },
  {
    group: "Walnut",
    entries: [
      ["walnut",       tokens.colors.walnut],
      ["walnut-deep",  tokens.colors.walnutDeep],
      ["walnut-soft",  tokens.colors.walnutSoft],
      ["walnut-tint",  tokens.colors.walnutTint],
    ],
  },
  {
    group: "Proof / verify / flag",
    entries: [
      ["proof",       tokens.colors.proof],
      ["proof-tint",  tokens.colors.proofTint],
      ["verify",      tokens.colors.verify],
      ["verify-tint", tokens.colors.verifyTint],
      ["flag",        tokens.colors.flag],
      ["flag-tint",   tokens.colors.flagTint],
    ],
  },
];

const TYPE_SAMPLES: Array<{ token: string; size: string; family: string; weight: string; demo: string; usage: string }> = [
  { token: "t-88", size: tokens.fontSize.t88, family: "display", weight: "700", demo: "Beirut",                         usage: "Title page" },
  { token: "t-66", size: tokens.fontSize.t66, family: "display", weight: "700", demo: "Beirut, spring of 1973",         usage: "H1 / chapter open" },
  { token: "t-49", size: tokens.fontSize.t49, family: "display", weight: "600", demo: "Two suitcases",                  usage: "H2" },
  { token: "t-37", size: tokens.fontSize.t37, family: "display", weight: "600", demo: "A misplaced conviction",         usage: "H3" },
  { token: "t-28", size: tokens.fontSize.t28, family: "display", weight: "500", demo: "Section title",                  usage: "H4" },
  { token: "t-21", size: tokens.fontSize.t21, family: "text",    weight: "400", demo: "Lead — generous breathing room", usage: "Lead / blockquote" },
  { token: "t-18", size: tokens.fontSize.t18, family: "text",    weight: "400", demo: "The manuscript itself, comfortable.", usage: "Editorial running text" },
  { token: "t-16", size: tokens.fontSize.t16, family: "ui",      weight: "400", demo: "UI body - IBM Plex Sans", usage: "UI body" },
  { token: "t-14", size: tokens.fontSize.t14, family: "ui",      weight: "500", demo: "Small UI",                       usage: "Buttons / chrome" },
  { token: "t-12", size: tokens.fontSize.t12, family: "mono",    weight: "500", demo: "VERIFIED · 14/14",               usage: "Eyebrow / proof label" },
];

export default function DesignSystemPage() {
  const [chapterTitle, setChapterTitle] = useState("Beirut, spring of 1973");
  const [dateline] = useState("2026-05-05  15:41 PDT");

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper text-ink-1 font-ui">
      {/* Header */}
      <header className="max-w-[1080px] mx-auto px-[24px] pt-[64px] pb-[32px] border-b border-rule">
        <Eyebrow withDot>Pull Press · Premium Publishing UI · Design System</Eyebrow>
        <h1 className="font-display font-bold text-t-66 leading-as-tight tracking-as-tight mt-[12px] mb-[16px]">
          The system, simplified.
        </h1>
        <p className="font-text text-t-21 leading-as-read text-ink-2 max-w-[680px]">
          Tokens flow from <code className="break-all font-mono text-t-14 text-walnut-deep">src/design-system/tokens.ts</code>
          {" "}into Tailwind, into CSS variables, into the React primitives below.
          Every value here is the canonical one - no other file in the codebase
          should declare a hex.
        </p>
      </header>

      <main className="max-w-[1080px] mx-auto px-[24px] py-[64px] space-y-[96px]">

        {/* COLOR */}
        <section>
          <Eyebrow withDot>1 · Color</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            Bone paper, deep ink, verdigris action, proof state.
          </h2>
          <div className="space-y-[32px]">
            {SWATCHES.map((row) => (
              <div key={row.group}>
                <Eyebrow>{row.group}</Eyebrow>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-[16px] mt-[12px]">
                  {row.entries.map(([name, hex]) => (
                    <div key={name} className="flex flex-col gap-[6px]">
                      <div
                        className="h-[64px] rounded-as-2 shadow-as-1"
                        style={{ background: hex }}
                      />
                      <div className="font-mono text-[10.5px] tracking-as-eyebrow uppercase text-ink-3">{name}</div>
                      <div className="font-mono text-[11px] text-ink-2">{hex}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* TYPE */}
        <section>
          <Eyebrow withDot>2 · Type</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            Source Serif 4 · IBM Plex Sans · IBM Plex Mono.
          </h2>
          <div className="space-y-[24px]">
            {TYPE_SAMPLES.map((s) => (
              <div key={s.token} className="grid grid-cols-1 gap-[8px] border-b border-rule-faint pb-[16px] sm:grid-cols-12 sm:items-baseline sm:gap-[16px]">
                <div className="min-w-0 font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3 sm:col-span-2">
                  {s.token}
                  <div className="text-ink-4 normal-case tracking-normal mt-[2px]">{s.family} · {s.weight}</div>
                </div>
                <div
                  className={`min-w-0 break-words sm:col-span-7 ${
                    s.family === "mono" ? "font-mono uppercase tracking-as-eyebrow"
                    : s.family === "ui" ? "font-ui"
                    : "font-display"
                  }`}
                  style={{ fontSize: s.size, lineHeight: 1.15, fontWeight: parseInt(s.weight) }}
                >
                  {s.demo}
                </div>
                <div className="min-w-0 font-ui text-t-13 text-ink-3 sm:col-span-3">{s.usage}</div>
              </div>
            ))}
          </div>
        </section>

        {/* SPACING + RADIUS */}
        <section>
          <Eyebrow withDot>3 · Spacing & radius</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            4 / 8 / 12 / 16 / 24 / 32 / 48 / 64 / 96 / 144.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-[48px]">
            <div>
              <Eyebrow>Spacing scale</Eyebrow>
              <div className="space-y-[8px] mt-[12px]">
                {Object.entries(tokens.spacing).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-[16px]">
                    <div className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-3 w-[40px]">{k}</div>
                    <div className="bg-walnut h-[6px] rounded-as-1" style={{ width: v }} />
                    <div className="font-mono text-[11px] text-ink-3">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Eyebrow>Radii</Eyebrow>
              <div className="grid grid-cols-5 gap-[16px] mt-[12px]">
                {Object.entries(tokens.radius).map(([k, v]) => (
                  <div key={k} className="flex flex-col items-center gap-[6px]">
                    <div className="bg-paper-raised shadow-as-2 w-[64px] h-[64px]" style={{ borderRadius: v }} />
                    <div className="font-mono text-[10.5px] tracking-as-eyebrow uppercase text-ink-3">{k}</div>
                    <div className="font-mono text-[10.5px] text-ink-3">{v}</div>
                  </div>
                ))}
              </div>

              <Eyebrow className="mt-[32px] block">Elevation</Eyebrow>
              <div className="grid grid-cols-3 gap-[16px] mt-[12px]">
                {(["s1", "s2", "s3"] as const).map((k) => (
                  <div key={k} className="flex flex-col items-center gap-[6px]">
                    <div
                      className="bg-paper-raised w-[88px] h-[64px] rounded-as-3"
                      style={{ boxShadow: tokens.shadow[k] }}
                    />
                    <div className="font-mono text-[10.5px] tracking-as-eyebrow uppercase text-ink-3">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* BUTTONS */}
        <section>
          <Eyebrow withDot>4 · Buttons</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            Sentence case. Editorial verbs.
          </h2>
          <Card>
            <Eyebrow>Variants</Eyebrow>
            <div className="flex gap-[10px] flex-wrap items-center mt-[12px]">
              <Button variant="primary">Lock the galley</Button>
              <Button variant="walnut">Run proof gate</Button>
              <Button variant="secondary">Open in reader</Button>
              <Button variant="ghost">Discard source</Button>
            </div>
            <Eyebrow className="mt-[24px] block">Sizes</Eyebrow>
            <div className="flex gap-[10px] items-center mt-[12px]">
              <Button size="sm">Small</Button>
              <Button size="md">Default</Button>
              <Button size="lg">Large</Button>
              <Button size="md" disabled>Disabled</Button>
            </div>
          </Card>
        </section>

        {/* INPUTS */}
        <section>
          <Eyebrow withDot>5 · Form inputs</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            Mono eyebrow · paper-raised field · walnut focus.
          </h2>
          <Card>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] max-w-[560px]">
              <Input
                label="Chapter title"
                value={chapterTitle}
                onChange={(e) => setChapterTitle(e.target.value)}
              />
              <Input
                label="Dateline"
                value={dateline}
                mono
                readOnly
              />
              <Input
                label="Take notes"
                placeholder="What changed in this source?"
                helper="Saved with the manuscript signature."
              />
            </div>
          </Card>
        </section>

        {/* BADGES */}
        <section>
          <Eyebrow withDot>6 · Proof-gate badges</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            Pills are reserved for state. Everything else is sharp.
          </h2>
          <Card>
            <div className="flex gap-[10px] flex-wrap">
              <Badge tone="verified">Verified</Badge>
              <Badge tone="in-review">Proof is open.</Badge>
              <Badge tone="take">Source 04</Badge>
              <Badge tone="contested">Contested</Badge>
              <Badge tone="draft">Raw source</Badge>
            </div>
          </Card>
        </section>

        {/* CARDS */}
        <section>
          <Eyebrow withDot>7 · Cards</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            Default · Chapter · Signature.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[16px]">
            <Card>
              <Eyebrow>Default</Eyebrow>
              <h4 className="font-display font-semibold text-t-21 mt-[8px]">A typical card</h4>
              <p className="font-text text-t-16 text-ink-2 mt-[6px]">
                Paper-raised tone, 8px radius, soft shadow, no border. The
                editorial default.
              </p>
            </Card>

            <Card variant="chapter">
              <Eyebrow>Chapter 04 · 4,212 words</Eyebrow>
              <h4 className="font-display font-semibold text-t-21 mt-[4px]">Beirut, spring of 1973</h4>
              <p className="font-text text-t-16 leading-as-ui text-ink-2 mt-[6px]">
                Two suitcases, a typewriter, and a misplaced conviction about
                how long the city would stay.
              </p>
              <div className="flex gap-[8px] items-center mt-[12px]">
                <Badge tone="verified">Verified</Badge>
                <Eyebrow>Take 04 · Dateline 03:41 PM</Eyebrow>
              </div>
            </Card>

            <Card variant="signature">
              <Eyebrow>Signature 02 of 06</Eyebrow>
              <h4 className="font-display font-semibold text-t-21 mt-[8px]">Pages 17–32</h4>
              <p className="font-text text-t-16 text-ink-2 mt-[6px]">
                A folded gathering. The right-edge strip suggests the spine
                fold of the printed signature.
              </p>
            </Card>
          </div>
        </section>

        {/* SAMPLE COMPOSITION */}
        <section>
          <Eyebrow withDot>8 · A composition</Eyebrow>
          <h2 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[8px] mb-[24px]">
            Everything together.
          </h2>
          <Card variant="chapter" className="max-w-[640px]">
            <Eyebrow>Chapter 04 · 4,212 words</Eyebrow>
            <h3 className="font-display font-semibold text-t-37 leading-as-snug tracking-as-snug mt-[6px] mb-[8px]">
              Beirut, spring of 1973
            </h3>
            <p className="font-text text-t-18 leading-as-read text-ink-2">
              Two suitcases, a typewriter, and a misplaced conviction about how
              long the city would stay. The flight from Athens was an old TWA
              707, and the cabin smelled of cologne and warm metal.
            </p>
            <div className="mt-[16px] flex flex-wrap gap-[8px] items-center">
              <Badge tone="verified">Verified</Badge>
                <Badge tone="take">Source 04</Badge>
              <span className="font-mono text-[11px] text-ink-3">·</span>
              <Eyebrow>Dateline 03:41 PM</Eyebrow>
            </div>
            <div className="mt-[16px] flex gap-[8px]">
              <Button variant="walnut">Run proof gate</Button>
              <Button variant="secondary">Open in reader</Button>
              <Button variant="ghost">Discard source</Button>
            </div>
          </Card>
        </section>

      </main>

      <footer className="max-w-[1080px] mx-auto px-[24px] py-[48px] border-t border-rule mt-[64px]">
        <Eyebrow>Source files</Eyebrow>
        <ul className="mt-[12px] space-y-[6px] break-words font-mono text-t-13 text-ink-2">
          <li>src/design-system/tokens.ts</li>
          <li>src/design-system/tokens.css</li>
          <li>src/components/ui/{`{Button,Card,Input,Badge,Eyebrow}`}.tsx</li>
          <li>tailwind.config.ts</li>
        </ul>
      </footer>
    </div>
  );
}
